import React from 'react';
import TabelaCrud from '../components/TabelaCrud';
import { formatarDataBR } from '../common/data';

/**
 * Calendário — eventos, reuniões, feriados e cronograma.
 *
 * A tela anterior enviava `creche_id` e `data`. A coluna é `data_inicio`, e é
 * NOT NULL: o INSERT morria e o gestor recebia "Internal server error" sem
 * saber o que fazer. Nenhum evento jamais foi cadastrado por aqui.
 *
 * Agora o formulário fala o mesmo idioma do backend, e traz os dois campos que
 * fazem o card do site ser verdade: para QUEM é o evento, e com quantos dias de
 * antecedência avisar.
 */

interface Evento {
  id: number;
  tipo: string;
  titulo: string;
  descricao: string | null;
  data_inicio: string;
  data_fim: string | null;
  ativo: boolean;
  publico: string;
  avisar_dias_antes: number | null;
  notificado_em: string | null;
}

const TIPOS = [
  { valor: 'evento', label: 'Evento' },
  { valor: 'reuniao', label: 'Reunião' },
  { valor: 'feriado', label: 'Feriado' },
  { valor: 'letivo', label: 'Dia letivo' },
  { valor: 'recesso', label: 'Recesso' },
  { valor: 'outro', label: 'Outro' },
];

const PUBLICOS = [
  { valor: 'todos', label: 'Equipe e famílias' },
  { valor: 'familias', label: 'Só as famílias' },
  { valor: 'equipe', label: 'Só a equipe' },
];

const rotulo = (l: { valor: string; label: string }[], v: string) =>
  l.find((i) => i.valor === v)?.label || v || '—';

export default function CalendarioPage() {
  return (
    <TabelaCrud<Evento>
      titulo="Calendário"
      endpoint="/calendario"
      modalLargo
      colunas={[
        { chave: 'data_inicio', label: 'Data', render: (e) => formatarDataBR(e.data_inicio) },
        { chave: 'titulo', label: 'Título' },
        { chave: 'tipo', label: 'Tipo', render: (e) => rotulo(TIPOS, e.tipo) },
        { chave: 'publico', label: 'Para quem', render: (e) => rotulo(PUBLICOS, e.publico) },
        {
          chave: 'avisar_dias_antes',
          label: 'Aviso',
          // Diz o estado do aviso, não só o número: "sem aviso" é uma escolha
          // legítima, e "já enviado" evita o gestor achar que falhou.
          render: (e) => {
            if (e.avisar_dias_antes === null || e.avisar_dias_antes === undefined) return 'sem aviso';
            const quando = e.avisar_dias_antes === 0
              ? 'no dia'
              : `${e.avisar_dias_antes} dia${e.avisar_dias_antes > 1 ? 's' : ''} antes`;
            return e.notificado_em ? `${quando} — já enviado` : quando;
          },
        },
        { chave: 'ativo', label: 'Ativo', render: (e) => (e.ativo ? 'sim' : 'não') },
      ]}
      itemVazio={{
        tipo: 'evento', titulo: '', descricao: '', data_inicio: '', data_fim: '',
        ativo: true, publico: 'todos', avisar_dias_antes: null,
      }}
      validateItem={(item) => {
        if (!item.titulo?.trim()) return 'Dê um título ao evento.';
        if (!item.data_inicio) return 'Informe a data do evento.';
        if (item.data_fim && item.data_fim < item.data_inicio) {
          return 'O término não pode ser anterior ao início.';
        }
        return null;
      }}
      renderFormulario={(item, onChange) => (
        <>
          <label>Título
            <input
              value={item.titulo ?? ''}
              placeholder="Ex.: Reunião de pais do Maternal"
              onChange={(e) => onChange('titulo', e.target.value)}
            />
          </label>
          <label>Tipo
            <select value={item.tipo ?? 'evento'} onChange={(e) => onChange('tipo', e.target.value)}>
              {TIPOS.map((t) => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </label>
          <label>Data
            <input
              type="date"
              value={item.data_inicio ?? ''}
              onChange={(e) => onChange('data_inicio', e.target.value)}
            />
          </label>
          <label>Termina em (deixe vazio se for de um dia só)
            <input
              type="date"
              value={item.data_fim ?? ''}
              onChange={(e) => onChange('data_fim', e.target.value || null)}
            />
          </label>
          <label>Para quem
            <select value={item.publico ?? 'todos'} onChange={(e) => onChange('publico', e.target.value)}>
              {PUBLICOS.map((p) => <option key={p.valor} value={p.valor}>{p.label}</option>)}
            </select>
          </label>
          <label>Avisar automaticamente
            <select
              value={item.avisar_dias_antes === null || item.avisar_dias_antes === undefined
                ? '' : String(item.avisar_dias_antes)}
              onChange={(e) => onChange('avisar_dias_antes', e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">Não avisar</option>
              <option value="0">No dia do evento</option>
              <option value="1">1 dia antes</option>
              <option value="3">3 dias antes</option>
              <option value="7">7 dias antes</option>
              <option value="15">15 dias antes</option>
              <option value="30">30 dias antes</option>
            </select>
          </label>
          <label>Descrição
            <textarea
              value={item.descricao ?? ''}
              rows={3}
              placeholder="O que a família e a equipe precisam saber."
              onChange={(e) => onChange('descricao', e.target.value)}
            />
          </label>
          <label>Ativo
            <select
              value={item.ativo === false ? 'nao' : 'sim'}
              onChange={(e) => onChange('ativo', e.target.value === 'sim')}
            >
              <option value="sim">Sim</option>
              <option value="nao">Não (desmarcado)</option>
            </select>
          </label>
          <p style={{ fontSize: 13, color: '#666' }}>
            O aviso é enviado uma única vez, pela manhã, para quem estiver no público escolhido.
            Mudar a data ou a antecedência reabilita o aviso com o novo combinado.
          </p>
        </>
      )}
    />
  );
}
